/* eslint-disable react-hooks/exhaustive-deps */
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { ElectricityAgreementForm, MeteringPoint } from "../../models";
import { FormControlElement, getEarliestPossibleAgreementStartDate, getMeteringPointWarningMessage } from "helpers";
import MeteringPointService from "services/electricity/electricity-metering-points.service";
import AddressAutocomplete from "components/AddressAutocomplete/AddressAutocomplete";

interface NewElectricityAgreementMeteringPointsProps {
    meteringPoints: MeteringPoint[];
    setMeteringPoints: (meteringPoints: MeteringPoint[]) => void;
    form: ElectricityAgreementForm;
    setForm: Dispatch<SetStateAction<ElectricityAgreementForm>>;
}

const NUMERIC_REGEX = /^\d*[.,]?\d*$/;

export default function NewElectricityAgreementMeteringPoints(
    { meteringPoints, setMeteringPoints, form, setForm }: NewElectricityAgreementMeteringPointsProps) {

    const [meteringPointsLoaded, setMeteringPointsLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setMeteringPointsLoaded(false);
        setMeteringPoints([]);
    }, [form.customerId])

    const onEleringRequest = () => {
        setError("");
        setMeteringPointsLoaded(false);
        setIsLoading(true);
        if (form.customerId) {
            MeteringPointService.getMeteringPointsExistingCustomer(form.customerId)
                .then(res => {
                    setMeteringPoints(res.meteringPoints);
                    setMeteringPointsLoaded(true);
                }).finally(() => setIsLoading(false))
        } else if (form.registryNumber) {
            const name = form.isCompany ? form.customerName : form.firstName + ' ' + form.lastName;
            MeteringPointService.getMeteringPointsNewCustomer(form.registryNumber, name || '')
                .then(res => {
                    setMeteringPoints(res.meteringPoints);
                    setMeteringPointsLoaded(true);
                }).catch(() => setError("Mõõtepunktide päring ebaõnnestus"))
                .finally(() => setIsLoading(false))
        }
    }

    function meteringPointHasAgreementOffer(meteringPoint: MeteringPoint){
        if (!meteringPoint.relatedAgreement) return false;

        const startDate = meteringPoint.relatedAgreement.startDate;
        if (startDate == null) return true;

        return false;
    }

    function onChange(eic: string){
        updateMeteringPointsState(
            meteringPoints.map(m => m.eic === eic ? ({...m, checked: !m.checked }) : m)
        );
    }

    function onChangeAll(){
        if(allMeteringPointsChecked){
            updateMeteringPointsState(
                meteringPoints.map(mp => ({...mp, checked: false }))
            )
            return;
        }

        updateMeteringPointsState(
            meteringPoints.map(m => ({...m, checked: !meteringPointHasAgreementOffer(m) ? true : false }))
        );
    }

    function updateMeteringPointsState(updatedMeteringPoints : MeteringPoint[]){
        setMeteringPoints(updatedMeteringPoints)

        const startDate = getEarliestPossibleAgreementStartDate(updatedMeteringPoints);
        const isJointInvoicePossible = updatedMeteringPoints.filter(mp => mp.checked).some(mp => mp.hasMatchingGridOperator)

        const hasJointInvoicePackageSelected = !!form.consumptionPackage.id && form.consumptionPackage.jointInvoicePackage;

        setForm({
            ...form,
            startDate,
            isJointInvoicePossible,
            consumptionPackage: !isJointInvoicePossible && hasJointInvoicePackageSelected ? {} : form.consumptionPackage
        })
    }

    const handleChangeProductionCapacity = (
        mp: MeteringPoint,
        e: React.ChangeEvent<FormControlElement>
    ) => {
        if (!NUMERIC_REGEX.test(e.target.value)) { return; }
        const normalizedValue = e.target.value.replace(',', '.');

        setMeteringPoints(
            meteringPoints.map(m => m.eic === mp.eic ? ({...m, productionCapacity: normalizedValue as any }) : m)
        )
    }
        
    const tableRows = meteringPoints.map(mp => {
        const isDisabled = meteringPointHasAgreementOffer(mp);

        return (
            <tr key={mp.eic}>
                <td>
                    <Form.Check
                        id={'check-' + mp.eic}
                        custom
                        type="checkbox"
                        className='checkbox-large'
                        label=''
                        onChange={() => onChange(mp.eic)}
                        checked={!!mp.checked}
                        disabled={isDisabled}
                    />
                </td>
                <td className={isDisabled ? 'text-gray-600' : ''}>
                    {mp.eic}
                </td>
                <td className={isDisabled ? 'text-gray-600' : ''}>
                    <AddressAutocomplete
                        required={mp.checked === true}
                        address={mp.address}
                        setAddress={(address) => setMeteringPoints(
                            meteringPoints.map(m => m.eic === mp.eic ? ({...m, address: address.fullAddress || address.address }) : m)
                        )}
                        disabled={isDisabled || !mp.checked}
                    />
                    {
                        mp.relatedAgreement ? 
                            (
                                getMeteringPointWarningMessage(
                                    {
                                        ...mp.relatedAgreement,
                                        isElectricity: true
                                    }
                                )
                            ) : null
                    }
                    {mp.terminationFee && (
                        <div className="font-weight-normal text-warning">Kehtiva lepingu lõpetamine on tasuline!</div>
                    )}
                </td>
                <td>
                {
                    form.sellCheck && (
                        <Form.Control
                            value={mp.productionCapacity}
                            onChange={(e) => handleChangeProductionCapacity(mp, e)}
                            type="text"
                            onWheel={(e: any) => e.target.blur()}
                            disabled={isDisabled}
                        />
                    )
                }
                </td>
            </tr>
            )
    });

    const allMeteringPointsChecked = !meteringPoints.some(mp => !mp.checked && !meteringPointHasAgreementOffer(mp));

    return (
        <>
            {tableRows.length ? <table className="new-electricity-agreement__metering-point-table">
                <thead>
                    <tr>
                        <th>
                            <Form.Check
                                id='check-all'
                                custom
                                type="checkbox"
                                className='checkbox-large'
                                label=''
                                onChange={() => onChangeAll()}
                                checked={allMeteringPointsChecked}
                            />
                        </th>
                        <th>EIC</th>
                        <th>AADRESS</th>
                        {
                            form.sellCheck && (
                                <th>TOOTMISVÕIMSUS(kW)</th>
                            )
                        }
                    </tr>
                </thead>
                <tbody>
                    { tableRows }
                </tbody>
            </table> : 
            <>
            { meteringPointsLoaded ?
                <div className="alert alert-info d-flex align-items-center text-dark"><i className='alert-icon icon__info--masked bg-dark mr-2'></i>Teil puuduvad mõõtepunktid.</div>
                : null
            }
            <button 
                type="button" 
                className="btn btn-outline-primary btn-lg" 
                onClick={onEleringRequest}
            >
                {
                    isLoading ? (
                        <Spinner size={"sm"} animation={"border"}/>
                    ) : <span>Tee päring Eleringi</span>
                }
            </button>
            {
                error ? 
                    <div className="text-danger mt-1">
                        Mõõtepunktide pärimine ebaõnnestus
                    </div> 
                : null
            }
            </>
            }
        </>
    )
}